import {
  ScreenerAnswer,
  ScreenerData,
  ScreenerQuestion,
  ScreenerSection,
} from "./types";
import {
  ConditionalDef,
  InterviewItemTemplate,
  OptionId,
  ScreenerQuestionRule,
} from "@/components/interview/types";
import { OptionsResolverUtils } from "@/components/interview/utils/optionsResolverUtils";
import type { TargetGroupId } from "@/components/outreach/panel/types";
import { isNullish } from "@/utils/helpers";
import { SurveyDataSingleLanguage } from "@/utils/types";

const EMPTY_SCREENER: ScreenerData = {
  sectionIds: [],
  sections: {},
  questions: {},
  answers: {},
};

// Whether the item is always shown for this target group based only on
// target-group membership (i.e. no answer/search-param/etc. criteria that the
// UI screener API can't represent). Audience conditionals are omitted here and
// re-screened on our platform with the rest of the interview.
const shownSolelyForTargetGroup = (
  conditional: ConditionalDef,
  targetGroupId: TargetGroupId
): boolean => {
  if (conditional.type === "conjunction") {
    return conditional.items.every((c) =>
      shownSolelyForTargetGroup(c, targetGroupId)
    );
  }
  if (conditional.type === "disjunction") {
    return conditional.items.some((c) =>
      shownSolelyForTargetGroup(c, targetGroupId)
    );
  }
  if (conditional.type === "target_group") {
    return conditional.target_group_ids.includes(targetGroupId);
  }
  // Any other criterion (audience, answer, search param, concept, segment)
  // can't be represented by target-group membership alone.
  return false;
};

export const convertStudyScreener = (
  surveyData: SurveyDataSingleLanguage,
  targetGroupId: TargetGroupId
): ScreenerData => {
  const screeningBlock = surveyData.blocks.find((b) => b.type === "screening");
  if (!surveyData.screener_data || !screeningBlock) {
    return EMPTY_SCREENER;
  }
  // UI's screener API can't represent conditional logic, so only include questions the launched target group always sees: unconditional ones, or ones shown solely via target-group membership. The rest are re-screened in our platform.
  // Forms (e.g. an NDA consent gate) carry no options / screener rule, so they would produce a malformed external screener question. They have no screen-out semantics; keep them out of the panel screener payload.
  const includedQuestions = screeningBlock.items.filter(
    (item) =>
      item.type !== "form_question" &&
      (isNullish(item.conditional) ||
        shownSolelyForTargetGroup(item.conditional, targetGroupId))
  );
  const sections: Record<string, ScreenerSection> = Object.fromEntries(
    includedQuestions.map((item) => [
      item.id,
      {
        title: item.text,
        questionIds: [item.id],
      },
    ])
  );
  const allTemplates = surveyData.blocks.flatMap((block) => block.items);
  const screenerRules = surveyData.screener_data?.screeningCriteria;
  const questions: Record<string, ScreenerQuestion> = Object.fromEntries(
    includedQuestions.map((item) => [
      item.id,
      questionFromInterviewItem(item, allTemplates, screenerRules),
    ])
  );
  const answers: Record<string, ScreenerAnswer> = includedQuestions.reduce(
    (acc, item) => {
      return {
        ...acc,
        ...answerFromInterviewItem(
          item,
          surveyData.screener_data?.screeningCriteria.find(
            (r) => r.interview_question_item === item.id
          ),
          allTemplates,
          screenerRules
        ),
      };
    },
    {}
  );
  return {
    sectionIds: includedQuestions.map((i) => i.id),
    sections,
    questions,
    answers,
  };
};

// User Interviews 422s when an answer we would send is blank. Omitted conditional questions never reach them.
export const getBlankUserInterviewsScreenerLaunchError = ({
  panel,
  surveyData,
  targetGroupId,
}: {
  panel: string;
  surveyData: SurveyDataSingleLanguage;
  targetGroupId: TargetGroupId;
}): string | null => {
  if (panel !== "user_interviews") return null;
  const screener = convertStudyScreener(surveyData, targetGroupId);
  for (const sectionId of screener.sectionIds) {
    const question = screener.questions[sectionId];
    if (!question) continue;
    const blankIndex = question.answerIds.findIndex((answerId) => {
      const answer = screener.answers[answerId];
      return answer !== undefined && answer.text.trim().length === 0;
    });
    if (blankIndex === -1) continue;
    const questionLabel = question.text.trim() || "Untitled screener question";
    return `Screener question "${questionLabel}" has an empty answer (option ${blankIndex + 1}). Fill it in or remove it before launching.`;
  }
  return null;
};

export const answerFromInterviewItem = (
  item: InterviewItemTemplate,
  screenerQuestionRule: ScreenerQuestionRule | undefined,
  allTemplates: InterviewItemTemplate[],
  screenerRules: ScreenerQuestionRule[] | undefined
): Record<string, ScreenerAnswer> => {
  const logicConversion = (id: OptionId) => {
    // if no screener rule, default to all options being "may select"
    if (!screenerQuestionRule) {
      return "may";
    }
    if (screenerQuestionRule.approveOptions.includes(id)) {
      // single select only allows "may"
      return item.multi_select ? "must_one_of" : "may";
    } else if (screenerQuestionRule.rejectOptions.includes(id)) {
      return "reject";
    }
    return "may";
  };
  const choices = OptionsResolverUtils.getOptions(
    item,
    allTemplates,
    screenerRules
  );
  return Object.fromEntries(
    choices.map((c) => [
      c.id,
      {
        text: c.text,
        qualifyLogic: logicConversion(c.id),
        responseClass: "answer",
      },
    ])
  );
};

export const questionFromInterviewItem = (
  item: InterviewItemTemplate,
  allTemplates: InterviewItemTemplate[],
  screenerRules: ScreenerQuestionRule[] | undefined
): ScreenerQuestion => {
  const choices = OptionsResolverUtils.getOptions(
    item,
    allTemplates,
    screenerRules
  );
  const pick = (() => {
    if (
      item.type === "question" &&
      item.questionType === "multiple_choice" &&
      item.multi_select
    ) {
      return "any" as const;
    } else if (item.type === "question" && item.questionType === "open_ended") {
      return "none" as const;
    }
    return "one" as const;
  })();
  return {
    text: item.text,
    answerIds:
      choices.length > 0
        ? choices.map((c) => c.id)
        : [
            // have to first cast as string to avoid type error, also this should only happen for open ended questions
            item.id as string as OptionId,
          ],
    isMandatory: true,
    pick,
  };
};
